import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';

import aiJobAssistantMutation from './aiJobAssistant.graphql';

// Typewriter timings (ms). Type each prompt char-by-char, hold when full, delete char-by-char, brief
// gap, then the next prompt — looping forever through the whole list.
const TYPE_MIN = 45;     // per-char typing speed (jittered up to TYPE_MAX)
const TYPE_MAX = 60;
const DELETE_MIN = 25;   // per-char deleting speed (jittered up to DELETE_MAX)
const DELETE_MAX = 35;
const HOLD_FULL = 1300;  // pause once a prompt is fully typed
const HOLD_EMPTY = 400;  // brief pause before the next prompt starts
const rand = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

// Slim hero variant of the AI job assistant. Lives inside the home hero glass card (no chat panel).
// On submit it calls the SAME `aiJobAssistant` (Claude) mutation the full AiAssistant uses, then mirrors
// its handling EXACTLY:
//   • serviceId  → stash the pre-fill in sessionStorage('aiPrefill') and route to /category?type=<id>
//                  (raw id — QuestionnaireFields matches the raw categoryId), so the customer lands on the
//                  normal confirmation/questionnaire already filled in. Draft only; never books or charges.
//   • followUp   → show the one short question inline; the next submit re-sends with `history` (one turn).
//   • error/503  → fall back to the old keyword route /category?type=<text> so the search never dead-ends.
//
// `prompts` is the full "describe your problem" example list from the hero. We OWN a typewriter
// animation over it here: type a prompt char-by-char, hold, delete, move to the next, loop forever.
// The partial string is shown as the input's `placeholder`. The animation freezes while the user is
// focused/typing (so it never fights them), respects prefers-reduced-motion (static example), and
// pauses while the tab is hidden. All timers are cleaned up on unmount; SSR renders the first prompt.
function HeroAiSearch({ prompts }) {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [matchNote, setMatchNote] = useState('');
  const [run, { loading }] = useMutation(aiJobAssistantMutation);

  // Normalize the prompt list (defensive: tolerate empty/undefined).
  const list = Array.isArray(prompts) && prompts.length ? prompts : [''];

  // Reduced-motion users (and SSR) get a single static example — no animation.
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // The animated placeholder string. Start with the first full prompt: SSR-safe (server renders this,
  // first client paint matches → no hydration mismatch) and a sensible reduced-motion fallback.
  const [typed, setTyped] = useState(list[0]);

  // The field is "engaged" when the user has typed text or is focused — freeze the animation then so
  // the placeholder doesn't compete with the user. Resume on blur if the field is empty.
  const [focused, setFocused] = useState(false);
  const engaged = focused || input.length > 0;

  const timerRef = useRef(null);

  useEffect(() => {
    // No animation when reduced-motion is requested, or there's nothing to cycle, or the user is
    // engaged with the field. Show the first full example statically in those cases.
    if (prefersReduced || list.length === 0 || engaged) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (engaged) setTyped(''); // clear so the user's own text/placeholder isn't shadowed
      else setTyped(list[0]);
      return undefined;
    }

    // State machine, kept in closure vars and scheduled via a single chained setTimeout.
    let promptIdx = 0;
    let charIdx = list[0].length; // start fully typed, then delete → next (smooth entry)
    let phase = 'hold-full';      // 'typing' | 'hold-full' | 'deleting' | 'hold-empty'
    let cancelled = false;

    const schedule = (fn, delay) => {
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        // Pause cycles while the tab is hidden — re-check shortly without advancing.
        if (typeof document !== 'undefined' && document.hidden) {
          schedule(fn, 300);
          return;
        }
        fn();
      }, delay);
    };

    const tick = () => {
      const current = list[promptIdx] || '';
      if (phase === 'typing') {
        charIdx += 1;
        setTyped(current.slice(0, charIdx));
        if (charIdx >= current.length) {
          phase = 'hold-full';
          schedule(tick, HOLD_FULL);
        } else {
          schedule(tick, rand(TYPE_MIN, TYPE_MAX));
        }
      } else if (phase === 'hold-full') {
        phase = 'deleting';
        schedule(tick, rand(DELETE_MIN, DELETE_MAX));
      } else if (phase === 'deleting') {
        charIdx -= 1;
        setTyped(current.slice(0, Math.max(charIdx, 0)));
        if (charIdx <= 0) {
          phase = 'hold-empty';
          promptIdx = (promptIdx + 1) % list.length;
          schedule(tick, HOLD_EMPTY);
        } else {
          schedule(tick, rand(DELETE_MIN, DELETE_MAX));
        }
      } else { // 'hold-empty'
        phase = 'typing';
        charIdx = 0;
        schedule(tick, rand(TYPE_MIN, TYPE_MAX));
      }
    };

    // Kick off from the fully-typed first prompt: hold, then delete, then loop.
    setTyped(list[0]);
    schedule(tick, HOLD_FULL);

    // Re-tick promptly when the tab becomes visible again (avoids a long stall after returning).
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden && !cancelled && timerRef.current === null) {
        schedule(tick, rand(TYPE_MIN, TYPE_MAX));
      }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReduced, engaged, list.length]);

  // Old keyword behaviour: route to the catalog/booking with the raw typed text (never dead-ends).
  const keywordFallback = (text) => {
    if (typeof window !== 'undefined') {
      const t = (text || '').trim();
      window.location.href = `/category?type=${encodeURIComponent(t || 'all')}`;
    }
  };

  // Reused verbatim from AiAssistant.book(): drop the pre-fill, then route to the service's booking.
  // `describe` seeds the booking's free-text "describe your job" field (AI summary, else the typed text).
  const routeToService = (serviceId, answers, describe) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(
        'aiPrefill',
        JSON.stringify({ categoryId: serviceId, answers: answers || '{}', describe: describe || '' }),
      );
    } catch (e) { /* ignore */ }
    window.location.href = `/category?type=${encodeURIComponent(String(serviceId))}`;
  };

  const submit = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setFollowUp('');
    setMatchNote('');
    // GA4 funnel: the hero bar is the home's primary action — count every real submit.
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'search', { search_term: text.slice(0, 100) });
    }
    try {
      const res = await run({ variables: { message: text, history: history || null } });
      const r = res && res.data && res.data.aiJobAssistant;
      // No key / error / non-200 (incl. status 503) → graceful keyword fallback.
      if (!r || r.status !== 200) {
        keywordFallback(text);
        return;
      }
      // Clear match → route straight to the pre-filled booking confirmation.
      if (r.serviceId) {
        setMatchNote(r.serviceName ? `Setting this up as: ${r.serviceName}…` : '');
        routeToService(r.serviceId, r.answers, r.summary || text);
        return;
      }
      // Need a clarification → ask the one short question inline and keep the turn going with history.
      if (r.followUpQuestion) {
        setHistory(`${history}Customer: ${text}\nAssistant: ${r.summary || ''} ${r.followUpQuestion || ''}\n`);
        setFollowUp(r.followUpQuestion);
        setInput('');
        return;
      }
      // Understood but nothing to route on → fall back so the customer still moves forward.
      keywordFallback(text);
    } catch (e) {
      keywordFallback(text);
    }
  };

  return (
    <div>
      <form
        className="aiSearchForm"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <input
          className="aiSearchInput"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={typed}
          maxLength={250}
          aria-label="Describe your problem"
          disabled={loading}
        />
        <button
          type="submit"
          className="aiSearchBtn"
          disabled={loading}
          aria-label={loading ? 'Searching…' : 'Search'}
        >
          {loading ? <span className="aiSpin" aria-hidden="true" /> : 'Search'}
        </button>
      </form>
      {followUp ? <p className="aiFollowUp">{followUp}</p> : null}
      {!followUp && matchNote ? <p className="aiMatchNote">{matchNote}</p> : null}
    </div>
  );
}

export default HeroAiSearch;
